import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useDeliveryTypeMultiFilters } from 'src/components/MainFilterBar/AdvancedSearch/useDeliveryTypeMultiFilters';
import { useMergedTagFilters } from 'src/components/MainFilterBar/Tags/useMergedTagFilters';
import { SellerAccountEmployeeMultiSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { Slider } from 'src/core/ui/Slider';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  EVENT_MAPPING_STATE_FILTER_TO_CID,
  EVENT_PRICED_STATUS_TO_CID,
  TICKET_TYPE_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
  YES_NO_INVERTED_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { InhandDateRangePresetNames } from 'src/utils/dateTimeUtils';
import {
  EventMappingStateFilter,
  FromEventMappingStateFilter,
  FromYesNoEnum,
  ToEventMappingStateFilter,
  ToYesNoEnum,
} from 'src/utils/eventQueryUtils';
import { formatNumber } from 'src/utils/numberFormatter';
import { ReportTypes } from 'src/utils/reportsUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingQuery,
  ListingQueryOperations,
  ListingQueryV2,
} from 'src/WebApiController';

import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
} from '../../../../components/Filters';
import { useDefaultYesNoFilterValues } from '../../../../components/Filters/hooks/useDefaultYesNoFilterValues';
import { RowFilter } from '../../../../components/Filters/RowFilter';
import { SectionFilter } from '../../../../components/Filters/SectionFilter';
import { PriceUpdatedDateStackedFilter } from '../../../../components/Filters/StackedFilter/PriceUpdatedDateStackedFilter';
import { SaleDateStackedFilter } from '../../../../components/Filters/StackedFilter/SaleDateStackedFilter';
import {
  CommonEventFiltersProps,
  useCommonEventFilters,
} from '../../../../components/MainFilterBar/AdvancedSearch/useCommonEventFilters';
import { usePurchaseInfoFilters } from '../../../../components/MainFilterBar/AdvancedSearch/usePurchaseInfoFilters';
import { MAX_NUMBER_OF_TICKETS_HELD } from '../../../../components/MainFilterBar/MainFilterBar.constants';
import { useAutoPricingResultFilter } from '../../../../components/MainFilterBar/useAutoPricingResultFilter';
import { useEventLastReviewedFilters } from '../../../../components/MainFilterBar/useEventLastReviewedFilter';
import {
  CurrencyFilterMultiSelector,
  CurrencyFilterSelector,
} from '../../../../components/Selectors/CurrencyFilterSelector';

export const listingsMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'eventDates',
];

export function useListingSearchFilters({
  isEventPage,
  isReportsPage,
  isHorizontalLayout,
  showCatalogFilters,
  showVenueFilter,
  showPerformerFilter,
  firstRenderOpen,
  supportMultiOperation,
}: {
  isEventPage?: boolean;
  isReportsPage?: boolean;
  isHorizontalLayout?: boolean;
  showCatalogFilters?: CommonEventFiltersProps<ListingQueryV2>['showCatalogFilters'];
  showVenueFilter?: CommonEventFiltersProps<ListingQueryV2>['showVenueFilter'];
  showPerformerFilter?: CommonEventFiltersProps<ListingQueryV2>['showPerformerFilter'];
  firstRenderOpen?: boolean;
  supportMultiOperation?: boolean;
}) {
  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<ListingQueryV2>();

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    reportType: isReportsPage ? ReportTypes.Inventory : undefined,
    useRelativePresets: isReportsPage,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Listing,
  });

  const eventLastReviewedFilters = useEventLastReviewedFilters({
    initialQuery,
    tempQuery,
    setTempQuery,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const hasFilterByCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByCurrencyCodeMulti
  );
  const hasFilterByCurrencyCodeFeature = !hasFilterByCurrencyCodeMultiFeature;

  const hasAutoFulfillSetting = useUserHasFeature(Feature.AutoFulfillSetting);

  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);

  const hasDateFiltersRelativeExactCustomFeature = useUserHasFeature(
    Feature.DateFiltersRelativeExactCustom
  );

  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({
    isReportsPage,
  });

  const { onUpdateResultTypes, pricingResultTypeToContentId } =
    useAutoPricingResultFilter(tempQuery, setTempQuery, supportMultiOperation);

  const autoPricingFilter = useMemo<FilterToolbarItem[]>(
    () => [
      {
        filterId: 'isAutoPricingEnabled',
        labelContentId: ContentId.AutoPricingEnabled,
        filterQueryKeys: ['isAutoPricingEnabled'],
        filterItem: (
          <PosEnumSelect
            style={{ width: '100%' }}
            value={ToYesNoEnum(tempQuery.isAutoPricingEnabled)}
            enableEmptySelection={shouldEnableEmptySelection(
              'isAutoPricingEnabled'
            )}
            placeholderText={ContentId.All}
            onChange={(yesNoEnumValue) => {
              if (
                yesNoEnumValue !== ToYesNoEnum(tempQuery.isAutoPricingEnabled)
              ) {
                setTempQuery({
                  ...tempQuery,
                  isAutoPricingEnabled: FromYesNoEnum(yesNoEnumValue),
                });
              }
            }}
            valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
          />
        ),
      },
      ...[
        {
          filterId: 'autoPricingResultTypes' as FilterToolbarItemId,
          labelContentId: ContentId.AutoPricingResultType,
          filterQueryKeys: ['autoPricingResultTypes'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumMultiSelect
              triggerProps={{ style: { width: '100%' } }}
              values={tempQuery.autoPricingResultTypes || []}
              placeholderText={ContentId.AutoPricingAllResultTypeOption}
              operation={tempQuery.operations?.autoPricingResultTypesOp ?? null}
              operationOptionsContent={
                supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
              }
              enableEmptySelection
              onChange={({ newValues: autoPricingResultTypes, operation }) => {
                onUpdateResultTypes(autoPricingResultTypes, operation ?? null);
              }}
              isHorizontalLayout={isHorizontalLayout}
              valueOptionsContent={pricingResultTypeToContentId}
            />
          ),
        },
      ],
    ],
    [
      isHorizontalLayout,
      onUpdateResultTypes,
      pricingResultTypeToContentId,
      setTempQuery,
      shouldEnableEmptySelection,
      supportMultiOperation,
      tempQuery,
    ]
  );

  const adminHoldFilter = useMemo<FilterToolbarItem[]>(
    () =>
      [
        {
          filterId: 'isAdminHold',
          labelContentId: ContentId.Hold,
          filterQueryKeys: ['isAdminHold'],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              value={ToYesNoEnum(tempQuery.isAdminHold)}
              placeholderText={ContentId.All}
              onChange={(yesNoEnumValue) => {
                if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isAdminHold)) {
                  setTempQuery({
                    ...tempQuery,
                    isAdminHold: FromYesNoEnum(yesNoEnumValue),
                  });
                }
              }}
              valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
            />
          ),
        },
      ] as FilterToolbarItem[],
    [setTempQuery, tempQuery]
  );

  const seatingFilters = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Seating,
      type: 'row',
      items: [
        {
          filterId: 'sectionContains',
          labelContentId: ContentId.Section,
          filterQueryKeys: [
            'sectionEquals',
            'sectionContains',
            'sectionNotContains',
          ],
          filterItem: (
            <SectionFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
        {
          filterId: 'rowContains',
          labelContentId: ContentId.Row,
          filterQueryKeys: ['rowEquals', 'rowContains', 'rowNotContains'],
          filterItem: (
            <RowFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
      ],
    }),
    [isHorizontalLayout, setTempQuery, tempQuery]
  );

  const additionalEventFilters = useMemo(
    () =>
      [
        ...eventLastReviewedFilters,
        {
          filterId: 'eventHasUnsoldTickets' as FilterToolbarItemId,
          labelContentId: ContentId.HasUnsoldListings,
          filterQueryKeys: ['eventHasUnsoldTickets'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              enableEmptySelection={shouldEnableEmptySelection(
                'eventHasUnsoldTickets'
              )}
              value={ToYesNoEnum(tempQuery.eventHasUnsoldTickets)}
              placeholderText={ContentId.All}
              onChange={(yesNoEnumValue) => {
                if (
                  yesNoEnumValue !==
                  ToYesNoEnum(tempQuery.eventHasUnsoldTickets)
                ) {
                  setTempQuery({
                    ...tempQuery,
                    eventHasUnsoldTickets: FromYesNoEnum(yesNoEnumValue),
                  });
                }
              }}
              valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
            />
          ),
        },
        {
          filterId: 'noSaleDates',
          labelContentId: ContentId.SaleDateFilterTitle,
          filterQueryKeys: ['lastSaleDates', 'noSaleDates'],
          filterItem: <SaleDateStackedFilter />,
        },
        {
          filterId: 'numberOfTicketsHeld',
          labelContentId: ContentId.NumberOfTicketsHeld,
          filterQueryKeys: ['numberOfTicketsHeld'],
          filterItem: (
            <Slider.Root
              min={0}
              max={MAX_NUMBER_OF_TICKETS_HELD}
              step={1}
              value={[
                tempQuery.numberOfTicketsHeld?.start ?? 0,
                tempQuery.numberOfTicketsHeld?.end ??
                  MAX_NUMBER_OF_TICKETS_HELD,
              ]}
              renderHandle={(n) => (
                <Slider.Handle>{formatNumber(n)}</Slider.Handle>
              )}
              onValueChange={([start, end]: [number, number]) => {
                if (
                  start !== (tempQuery.numberOfTicketsHeld?.start || 0) ||
                  end !== tempQuery.numberOfTicketsHeld?.end
                ) {
                  const newQuery = {
                    ...tempQuery,
                    numberOfTicketsHeld: {
                      start: start === 0 ? null : start,
                      end: end === MAX_NUMBER_OF_TICKETS_HELD ? null : end,
                    },
                  };
                  setTempQuery(newQuery);
                }
              }}
            />
          ),
        },
        {
          filterId: 'isMapped' as FilterToolbarItemId,
          labelContentId: ContentId.RequestState,
          filterQueryKeys: [
            'isMapped',
            'isMappingRejected',
          ] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              enableEmptySelection={shouldEnableEmptySelection('isMapped')}
              value={ToEventMappingStateFilter(
                tempQuery.isMapped,
                tempQuery.isMappingRejected
              )}
              placeholderText={ContentId.All}
              onChange={(filterEnumValue) => {
                if (
                  filterEnumValue !==
                  ToEventMappingStateFilter(
                    tempQuery.isMapped,
                    tempQuery.isMappingRejected
                  )
                ) {
                  const [isMapped, isMappingRejected] =
                    FromEventMappingStateFilter(
                      filterEnumValue ?? EventMappingStateFilter.All
                    );
                  setTempQuery({
                    ...tempQuery,
                    isMapped,
                    isMappingRejected,
                  });
                }
              }}
              valueOptionsContent={EVENT_MAPPING_STATE_FILTER_TO_CID}
            />
          ),
        },
        {
          filterId: 'eventUnpricedStatuses' as FilterToolbarItemId,
          labelContentId: ContentId.HasUnpricedListings,
          filterQueryKeys: ['eventUnpricedStatuses'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumMultiSelect
              style={{ width: '100%' }}
              values={tempQuery.eventUnpricedStatuses ?? []}
              enableEmptySelection={shouldEnableEmptySelection(
                'eventUnpricedStatuses'
              )}
              operation={tempQuery.operations?.eventUnpricedStatusOp ?? null}
              operationOptionsContent={
                supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
              }
              isHorizontalLayout={isHorizontalLayout}
              onChange={({ newValues: eventUnpricedStatuses, operation }) => {
                if (
                  !isEqual(
                    eventUnpricedStatuses,
                    tempQuery.eventUnpricedStatuses
                  ) ||
                  operation !== tempQuery.operations?.eventUnpricedStatusOp
                ) {
                  setTempQuery({
                    ...tempQuery,
                    eventUnpricedStatuses: eventUnpricedStatuses?.length
                      ? eventUnpricedStatuses
                      : null,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as ListingQueryOperations),
                          eventUnpricedStatusOp: operation ?? null,
                        }
                      : null,
                  });
                }
              }}
              valueOptionsContent={EVENT_PRICED_STATUS_TO_CID}
            />
          ),
        },
      ] as FilterToolbarItem[],
    [
      eventLastReviewedFilters,
      isHorizontalLayout,
      setTempQuery,
      shouldEnableEmptySelection,
      supportMultiOperation,
      tempQuery,
    ]
  );

  const eventFilters = useCommonEventFilters({
    initialQuery,
    setQuery: setTempQuery,
    query: tempQuery,
    showCatalogFilters,
    showVenueFilter,
    showPerformerFilter,
    firstRenderOpen,
    additionalFilters: additionalEventFilters,
    supportMultiOperation,
    isHorizontalLayout,
  });
  const eventFilter = isEventPage ? undefined : eventFilters;

  const notesFilters = useInternalNotesFilter<ListingQuery>({
    entityType: ActionOutboxEntityType.Listing,
    isHorizontalLayout,
  });

  const deliveryTypeFilter = useDeliveryTypeMultiFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const metricsIconNote = useContent(ContentId.ThisFilterWillChangeMetrics);

  const filters = useMemo<FilterToolbarGroup[]>(
    () =>
      [
        eventFilter,
        {
          titleContentId: ContentId.Pricing,
          type: 'row',
          items: [
            {
              filterId: 'pricerSellerUserIds' as FilterToolbarItemId,
              filterQueryKeys: ['pricerSellerUserIds'] as FilterToolbarItemId[],
              labelContentId: ContentId.PricedBy,
              filterItem: (
                <SellerAccountEmployeeMultiSelector
                  values={tempQuery.pricerSellerUserIds ?? []}
                  operation={tempQuery.operations?.pricerSellerUserIdOp ?? null}
                  operationOptionsContent={
                    supportMultiOperation
                      ? COLLECTION_FILTER_OP_TO_CID
                      : undefined
                  }
                  isHorizontalLayout={isHorizontalLayout}
                  enableEmptySelection
                  style={{ width: '100%' }}
                  onChange={({ newValues, operation }) => {
                    let newPricerSellerUserIds: string[] | null = newValues
                      .filter((value) => value !== EMPTY_GUID)
                      .map((value) => value.trim());

                    if (!newPricerSellerUserIds?.length) {
                      newPricerSellerUserIds = null;
                    }

                    if (
                      !isEqual(
                        newPricerSellerUserIds,
                        tempQuery.pricerSellerUserIds
                      ) ||
                      operation !== tempQuery.operations?.pricerSellerUserIdOp
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        pricerSellerUserIds: newPricerSellerUserIds,
                        operations: supportMultiOperation
                          ? {
                              ...(tempQuery.operations as ListingQueryOperations),
                              pricerSellerUserIdOp: operation ?? null,
                            }
                          : null,
                      });
                    }
                  }}
                />
              ),
            },
            ...(hasAutoPricingFeature ? autoPricingFilter : []),

            {
              filterId: 'isPricedAboveZero' as FilterToolbarItemId,
              labelContentId: ContentId.Priced,
              filterQueryKeys: ['isPricedAboveZero'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPricedAboveZero)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPricedAboveZero'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isPricedAboveZero)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPricedAboveZero: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'lastPriceUpdateDates' as FilterToolbarItemId,
              filterQueryKeys: [
                'lastPriceUpdateDates',
                'lastPriceNoUpdateDates',
              ] as FilterToolbarItemId[],
              labelContentId: ContentId.PriceDateFilterTitle,
              filterItem: <PriceUpdatedDateStackedFilter />,
            },

            {
              labelContentId: ContentId.IsPricedByMarketplace,
              filterId: 'isPricedByMarketplace' as FilterToolbarItemId,
              filterQueryKeys: [
                'isPricedByMarketplace',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPricedByMarketplace)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPricedByMarketplace'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isPricedByMarketplace)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPricedByMarketplace: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },

            ...(hasFilterByCurrencyCodeFeature
              ? [
                  {
                    filterId: 'currencyCode' as FilterToolbarItemId,
                    labelContentId: ContentId.Currency,
                    filterQueryKeys: ['currencyCode'] as FilterToolbarItemId[],
                    filterItem: (
                      <CurrencyFilterSelector
                        value={tempQuery.currencyCode ?? ''}
                        style={{ width: '100%' }}
                        placeholderText={ContentId.All}
                        onChange={(newCurrencyCode) => {
                          if (tempQuery.currencyCode !== newCurrencyCode) {
                            setTempQuery({
                              ...tempQuery,
                              currencyCode: newCurrencyCode?.length
                                ? newCurrencyCode
                                : null,
                            });
                          }
                        }}
                        enableEmptySelection
                      />
                    ),
                  },
                ]
              : []),
            ...(hasFilterByCurrencyCodeMultiFeature
              ? [
                  {
                    filterId: 'currencyCodes' as FilterToolbarItemId,
                    labelContentId: ContentId.Currency,
                    filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
                    filterItem: (
                      <CurrencyFilterMultiSelector
                        values={tempQuery.currencyCodes ?? []}
                        triggerProps={{ style: { width: '100%' } }}
                        placeholderText={ContentId.All}
                        operation={
                          tempQuery.operations?.currencyCodesOp ?? null
                        }
                        operationOptionsContent={
                          supportMultiOperation
                            ? COLLECTION_FILTER_OP_TO_CID
                            : undefined
                        }
                        isHorizontalLayout={isHorizontalLayout}
                        onChange={({
                          newValues: newCurrencyCodes,
                          operation,
                        }) => {
                          if (
                            !isEqual(
                              tempQuery.currencyCodes,
                              newCurrencyCodes
                            ) ||
                            operation !== tempQuery.operations?.currencyCodesOp
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              currencyCodes: newCurrencyCodes?.length
                                ? newCurrencyCodes
                                : null,
                              operations: supportMultiOperation
                                ? {
                                    ...(tempQuery.operations as ListingQueryOperations),
                                    currencyCodesOp: operation ?? null,
                                  }
                                : null,
                            });
                          }
                        }}
                        enableEmptySelection
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          titleContentId: ContentId.Broadcast,
          type: 'row',
          items: [
            {
              filterId: 'isListed' as FilterToolbarItemId,
              labelContentId: ContentId.Broadcasted,
              filterQueryKeys: ['isListed'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isListed)}
                  enableEmptySelection={shouldEnableEmptySelection('isListed')}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isListed)) {
                      setTempQuery({
                        ...tempQuery,
                        isListed: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'hasBroadcastError' as FilterToolbarItemId,
              labelContentId: ContentId.HasBroadcastError,
              filterQueryKeys: ['hasBroadcastError'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.hasBroadcastError)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'hasBroadcastError'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.hasBroadcastError)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasBroadcastError: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            ...adminHoldFilter,
            {
              filterId: 'isDuplicate' as FilterToolbarItemId,
              labelContentId: ContentId.IsDuplicate,
              filterQueryKeys: ['isDuplicate'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isDuplicate)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isDuplicate'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isDuplicate)) {
                      setTempQuery({
                        ...tempQuery,
                        isDuplicate: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },

            {
              filterId: 'isPendingProof' as FilterToolbarItemId,
              labelContentId: ContentId.ProofOfPurchasedRequired,
              filterQueryKeys: ['isPendingProof'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPendingProof)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPendingProof'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !== ToYesNoEnum(tempQuery.isPendingProof)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPendingProof: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
          ],
        },
        {
          titleContentId: ContentId.Fulfillment,
          type: 'row',
          items: [
            {
              filterId: 'isPredelivered' as FilterToolbarItemId,
              labelContentId: ContentId.PreDelivered,
              type: 'row',
              filterQueryKeys: ['isPredelivered'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPredelivered)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPredelivered'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !== ToYesNoEnum(tempQuery.isPredelivered)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPredelivered: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            deliveryTypeFilter,
            {
              filterId: 'ticketTypes' as FilterToolbarItemId,
              labelContentId: ContentId.TicketType,
              filterQueryKeys: ['ticketTypes'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumMultiSelect
                  style={{ width: '100%' }}
                  values={tempQuery.ticketTypes ?? []}
                  operation={tempQuery.operations?.ticketTypeOp ?? null}
                  operationOptionsContent={
                    supportMultiOperation
                      ? COLLECTION_FILTER_OP_TO_CID
                      : undefined
                  }
                  isHorizontalLayout={isHorizontalLayout}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'ticketTypes'
                  )}
                  placeholderText={ContentId.AllTicketTypes}
                  onChange={({ newValues: ticketTypes, operation }) => {
                    if (
                      !isEqual(ticketTypes, tempQuery.ticketTypes) ||
                      operation !== tempQuery.operations?.ticketTypeOp
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        ticketTypes: ticketTypes?.length ? ticketTypes : null,
                        operations: supportMultiOperation
                          ? {
                              ...(tempQuery.operations as ListingQueryOperations),
                              ticketTypeOp: operation ?? null,
                            }
                          : null,
                      });
                    }
                  }}
                  valueOptionsContent={TICKET_TYPE_TO_CID}
                />
              ),
            },
            ...(hasAutoFulfillSetting
              ? [
                  {
                    filterId: 'isNoFulfill' as FilterToolbarItemId,
                    labelContentId: ContentId.AutoFulfill,
                    type: 'row',
                    filterQueryKeys: ['isNoFulfill'] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(
                          // the ui is the inverse of isNoFulfil
                          tempQuery.isNoFulfill == null
                            ? null
                            : !tempQuery.isNoFulfill
                        )}
                        placeholderText={ContentId.All}
                        enableEmptySelection={shouldEnableEmptySelection(
                          'isNoFulfill'
                        )}
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(
                              // the ui is the inverse of isNoFulfil
                              tempQuery.isNoFulfill == null
                                ? null
                                : !tempQuery.isNoFulfill
                            )
                          ) {
                            const v = FromYesNoEnum(yesNoEnumValue);
                            setTempQuery({
                              ...tempQuery,
                              isNoFulfill: v == null ? null : !v, // the ui is the inverse of isNoFulfil
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                ]
              : []),
            {
              filterId: 'hasUnsoldTickets' as FilterToolbarItemId,
              labelContentId: ContentId.HasUnsoldTickets,
              filterQueryKeys: ['hasUnsoldTickets'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.hasUnsoldTickets)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'hasUnsoldTickets'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !== ToYesNoEnum(tempQuery.hasUnsoldTickets)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasUnsoldTickets: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'inHandDates' as const,
              labelContentId: ContentId.InHand,
              filterQueryKeys: ['inHandDates'] as FilterToolbarItemId[],
              filterItem: (
                <DateRangeSelector
                  useRelativePresets={
                    // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                    hasDateFiltersRelativeExactCustomFeature ? false : true
                  }
                  presetNames={InhandDateRangePresetNames}
                  exactPresetNames={
                    hasDateFiltersRelativeExactCustomFeature
                      ? InhandDateRangePresetNames
                      : undefined
                  }
                  trailingPresetNames={
                    hasDateFiltersRelativeExactCustomFeature
                      ? InhandDateRangePresetNames
                      : undefined
                  }
                  value={tempQuery.inHandDates}
                  defaultValue={initialQuery.inHandDates}
                  onBlur={({ value, operation }) =>
                    setTempQuery({
                      ...tempQuery,
                      inHandDates: value,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as ListingQueryOperations),
                            inHandDatesOp: operation ?? null,
                          }
                        : null,
                    })
                  }
                  operation={tempQuery.operations?.inHandDatesOp ?? null}
                  enableOperationSelect={supportMultiOperation}
                  isHorizontalLayout={isHorizontalLayout}
                />
              ),
            },
            ...(hasSeatSaverFeature
              ? [
                  {
                    filterId: 'isSeatSaver' as const,
                    labelContentId: ContentId.Placeholder,
                    filterQueryKeys: ['isSeatSaver'] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(tempQuery.isSeatSaver)}
                        enableEmptySelection={shouldEnableEmptySelection(
                          'isSeatSaver'
                        )}
                        placeholderText={ContentId.All}
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.isSeatSaver)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              isSeatSaver: FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          titleContentId: ContentId.Mappings,
          type: 'row',
          items: [
            {
              filterId: 'isMapped' as FilterToolbarItemId,
              labelContentId: ContentId.EventMapped,
              filterQueryKeys: ['isMapped'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isMapped)}
                  enableEmptySelection={shouldEnableEmptySelection('isMapped')}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isMapped)) {
                      setTempQuery({
                        ...tempQuery,
                        isMapped: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'isSeatingUnmapped' as FilterToolbarItemId,
              labelContentId: ContentId.SeatingMapped,
              filterQueryKeys: ['isSeatingUnmapped'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isSeatingUnmapped)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isSeatingUnmapped'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isSeatingUnmapped)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isSeatingUnmapped: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_INVERTED_ENUM_FILTER_TO_CID}
                />
              ),
            },
          ],
        },
        seatingFilters,
        purchaseInfoFilters,
        notesFilters,
        ...tagsFilter,
      ]
        .filter((f) => f)
        .map((f) => f!),
    [
      eventFilter,
      tempQuery,
      supportMultiOperation,
      isHorizontalLayout,
      hasAutoPricingFeature,
      autoPricingFilter,
      shouldEnableEmptySelection,
      hasFilterByCurrencyCodeFeature,
      hasFilterByCurrencyCodeMultiFeature,
      adminHoldFilter,
      deliveryTypeFilter,
      hasAutoFulfillSetting,
      hasDateFiltersRelativeExactCustomFeature,
      initialQuery.inHandDates,
      hasSeatSaverFeature,
      seatingFilters,
      purchaseInfoFilters,
      notesFilters,
      tagsFilter,
      setTempQuery,
    ]
  );

  return { filters };
}
